import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useSchedulersByParticipant } from "./useSchedulers";
import { schedulerSlotRef } from "../lib/data/schedulers";
import { db } from "../lib/firebase";

/**
 * Returns the user's FINALIZED schedulers paired with their winning slot's
 * times AND a `hidden` flag derived from the user's own vote on that
 * scheduler. Used by the availability page in lieu of
 * `usersPublic.busyWindows`, which only gets populated for users who voted
 * attending — creators who never voted on their own poll's winning slot
 * would otherwise be missing entirely.
 *
 * `hidden` is true only on an explicit-NO signal:
 *   - voteDoc.rsvp === "CANT_GO"  (the post-finalization RSVP path)
 *   - voteDoc.noTimesWork === true (legacy "no times work for me")
 * No vote, or a vote that doesn't include the winning slot, is treated as
 * "maybe" and emitted as a non-hidden conflict.
 *
 * Real-time: parent query is live, plus we subscribe to each finalized
 * scheduler's winning slot doc + the user's own vote doc so RSVP changes
 * propagate without a refresh.
 *
 * Shape: { data: Array<{
 *   schedulerId, title, startUtc, endUtc, winningSlotId, hidden,
 *   questingGroupId, questingGroupName, googleCalendarId, googleEventId,
 *   creatorId, calendarInvitedUids
 * }> }
 *
 * The trailing fields are pass-throughs from the parent scheduler doc so
 * consumers (the /availability page in particular) can render the
 * questing-group chip + a "Open in Google Calendar" icon next to each
 * upcoming session, and color the calendar-grid event by group color —
 * mirroring the dashboard's session-card treatment. `creatorId` +
 * `calendarInvitedUids` let consumers gate the "View calendar invite"
 * deep-link to viewers actually invited as attendees (identity/email-privacy
 * initiative, Model Y) via isViewerCalendarInvited().
 */
export function useFinalizedSessions(uid) {
  const { data: allSchedulers, loading } = useSchedulersByParticipant(uid);
  const [slotsBySchedulerId, setSlotsBySchedulerId] = useState({});
  const [voteBySchedulerId, setVoteBySchedulerId] = useState({});

  const finalized = useMemo(
    () =>
      (allSchedulers || []).filter(
        (s) => s.status === "FINALIZED" && s.winningSlotId
      ),
    [allSchedulers]
  );

  // Stable cache key for the subscription effect — only changes when the
  // (id, winningSlotId) set changes, so we don't churn subscriptions on
  // every parent re-render.
  const subKey = useMemo(
    () => finalized.map((s) => `${s.id}:${s.winningSlotId}`).join("|"),
    [finalized]
  );

  useEffect(() => {
    if (!uid || finalized.length === 0) {
      setSlotsBySchedulerId({});
      setVoteBySchedulerId({});
      return undefined;
    }
    const unsubs = [];
    for (const s of finalized) {
      const slotRefDoc = schedulerSlotRef(s.id, s.winningSlotId);
      unsubs.push(
        onSnapshot(
          slotRefDoc,
          (snap) => {
            const data = snap.data();
            setSlotsBySchedulerId((prev) => ({
              ...prev,
              [s.id]: data || null,
            }));
          },
          () => {
            // Slot reads can be denied for legacy states — best-effort.
          }
        )
      );

      const voteRef = doc(db, "schedulers", s.id, "votes", uid);
      unsubs.push(
        onSnapshot(
          voteRef,
          (snap) => {
            const data = snap.exists() ? snap.data() : null;
            setVoteBySchedulerId((prev) => ({
              ...prev,
              [s.id]: data,
            }));
          },
          () => {
            // Vote read failure — treat as "no vote" (maybe → not hidden).
          }
        )
      );
    }
    return () => {
      for (const u of unsubs) u();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subKey, uid]);

  const data = useMemo(() => {
    return finalized
      .map((s) => {
        const slot = slotsBySchedulerId[s.id];
        if (!slot?.start || !slot?.end) return null;
        const vote = voteBySchedulerId[s.id];
        const hidden =
          vote?.rsvp === "CANT_GO" || vote?.noTimesWork === true;
        return {
          schedulerId: s.id,
          title: s.title || "Finalized session",
          startUtc: slot.start,
          endUtc: slot.end,
          winningSlotId: s.winningSlotId,
          hidden,
          questingGroupId: s.questingGroupId || null,
          questingGroupName: s.questingGroupName || null,
          googleCalendarId: s.googleCalendarId || null,
          googleEventId: s.googleEventId || null,
          creatorId: s.creatorId || null,
          // Preserve arrays verbatim (incl. empty) so a real invited set gates
          // correctly; a missing field stays non-array → legacy "show" fallback.
          calendarInvitedUids: Array.isArray(s.calendarInvitedUids)
            ? s.calendarInvitedUids
            : null,
        };
      })
      .filter(Boolean);
  }, [finalized, slotsBySchedulerId, voteBySchedulerId]);

  return { data, loading };
}
